"use client";

import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import {
  ShoppingCart,
  Tv,
  Pill,
  Activity,
  Shield,
  Zap,
  Building2,
  Factory,
  FlaskConical,
  Package,
} from "lucide-react";
import abisData from "@data/landing-pages/agentic-bi-tool.json";

interface IndustryMenuProps {
  activeType?: string; // z.B. "pharma"
  className?: string;
  availableCategories?: string[]; // Array von Category-IDs, die verfügbare Items haben (z.B. ["pharma", "energy"])
  variant?: "default" | "primary"; // Variante: default = weiß/grau, primary = Primary-Farben mit Transparenz
  align?: "left" | "center"; // Ausrichtung: left = links, center = zentriert (default: center auf Overview, left auf Slug-Unterseiten)
}

// Icon mapping - konsistent mit TargetAudienceSection
const iconMap: {
  [key: string]: React.ComponentType<{ className?: string }>;
} = {
  "shopping-cart": ShoppingCart,
  tv: Tv,
  pill: Pill,
  "building-2": Building2,
  factory: Factory,
  activity: Activity,
  shield: Shield,
  zap: Zap,
  flask: FlaskConical,
  truck: Package,
};

// Mapping zwischen type und targetGroup.id
const typeToIdMap: { [key: string]: string } = {
  pharma: "pharma",
  energy: "energy",
  manufacturing: "manufacturing",
  chemical: "chemical",
  retail: "retail",
  logistics: "logistics",
  media: "media",
  insurance: "insurance",
  enterprises: "enterprises",
};

// Mapping zwischen targetGroup.id und slug
const idToSlugMap: { [key: string]: string } = {
  pharma: "pharma",
  energy: "energy",
  manufacturing: "manufacturing",
  chemical: "chemical",
  retail: "retail",
  logistics: "logistics",
  media: "media",
  insurance: "insurance",
  enterprises: "enterprises",
};

export default function IndustryMenu({
  activeType,
  className = "",
  availableCategories = [], // Standard: alle Categories verfügbar
  variant = "default", // Default: weiß/grau, primary: Primary-Farben
  align, // Explizite Ausrichtung (optional, sonst automatisch basierend auf activeType)
}: IndustryMenuProps) {
  const targetGroups = abisData.targetAudience.targetGroups;

  // Filtere nur relevante Branchen (optional: alle anzeigen oder nur die mit Success Stories)
  // Für jetzt zeigen wir alle Branchen an
  const displayGroups = targetGroups.filter((group) => {
    // Zeige nur Branchen, die auch im typeToIdMap existieren
    return typeToIdMap.hasOwnProperty(group.id);
  });

  const getIconComponent = (iconName: string) => {
    const IconComponent = iconMap[iconName];
    if (!IconComponent) {
      return Activity; // Fallback
    }
    return IconComponent;
  };

  const isActive = (groupId: string) => {
    if (!activeType) return false;
    const mappedId = typeToIdMap[activeType];
    return mappedId === groupId;
  };

  const getSlug = (groupId: string) => {
    return idToSlugMap[groupId] || groupId;
  };

  // Sortiere: Pharma zuerst (wenn verfügbar), dann aktiver Button, dann alphabetisch
  const sortedGroups = [...displayGroups].sort((a, b) => {
    const aActive = isActive(a.id);
    const bActive = isActive(b.id);
    const aIsPharma = a.id === "pharma";
    const bIsPharma = b.id === "pharma";

    // Pharma hat höchste Priorität
    if (aIsPharma && !bIsPharma) return -1;
    if (!aIsPharma && bIsPharma) return 1;

    // Dann aktiver Button
    if (aActive && !bActive) return -1;
    if (!aActive && bActive) return 1;

    // Wenn beide gleich aktiv/inaktiv sind, alphabetisch sortieren
    return a.title.localeCompare(b.title, "de");
  });

  // Scroll-Animation State
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Intersection Observer für Scroll-Animationen
  useEffect(() => {
    const currentContainer = containerRef.current;

    if (!currentContainer) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          } else {
            // Optional: Reset beim Verlassen des Viewports
            // setIsVisible(false);
          }
        });
      },
      {
        threshold: 0.1, // Trigger wenn 10% sichtbar
        rootMargin: "0px 0px -50px 0px", // Trigger etwas früher
      }
    );

    observer.observe(currentContainer);

    return () => {
      observer.unobserve(currentContainer);
    };
  }, []);

  // Sortiere alle Buttons: Aktiver zuerst, dann aktivierbare, dann deaktivierte
  const allButtonsSorted = sortedGroups
    .map((group) => {
      const hasItems =
        availableCategories.length === 0 ||
        availableCategories.includes(group.id);
      const active = isActive(group.id);

      return {
        ...group,
        hasItems,
        isDisabled: !hasItems,
        isActive: active,
        priority: active ? 0 : hasItems ? 1 : 2, // Aktiver = 0, aktivierbar = 1, deaktiviert = 2
      };
    })
    .sort((a, b) => {
      // Sortiere nach Priority (aktiver zuerst)
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }
      // Wenn gleiche Priority, alphabetisch
      return a.title.localeCompare(b.title, "de");
    });

  // Bestimme Ausrichtung: Explizit gesetzt oder automatisch basierend auf activeType
  const alignmentClass = align
    ? align === "left"
      ? "justify-start"
      : "justify-center"
    : activeType
    ? "justify-start"
    : "justify-center";

  // Entferne justify-center/justify-start aus className falls vorhanden, um Konflikte zu vermeiden
  const cleanedClassName = className
    .replace(/\bjustify-(start|center)\b/g, "")
    .trim();

  return (
    <nav
      ref={containerRef}
      className={`flex items-center mb-6 ${alignmentClass} ${cleanedClassName}`}
      aria-label="Branchen Navigation"
    >
      {/* Alle Buttons - Mobile: leicht überlappend in einer Zeile, Desktop: normaler Abstand */}
      <div className="flex items-center -space-x-2 sm:space-x-0 sm:gap-1.5 md:gap-1.5 lg:gap-2">
        {allButtonsSorted.map((group, index) => {
          const IconComponent = getIconComponent(group.icon);
          const active = group.isActive;
          const slug = getSlug(group.id);
          const animationDelay = index * 40;
          const href = group.hasItems
            ? `/success-stories/${slug}`
            : "/success-stories";

          return (
            <Link
              key={group.id}
              href={href}
              className={`
                flex items-center justify-center min-w-[44px] min-h-[44px] w-11 h-11 rounded-full border-2 
                transition-all duration-300 relative
                ${
                  active
                    ? "bg-abis-accent text-abis-primary border-abis-accent shadow-lg shadow-abis-accent/30"
                    : "bg-white/90 text-gray-700 border-white/80 hover:bg-white hover:border-white hover:shadow-md"
                }
                ${
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-4"
                }
              `}
              style={{
                transitionDelay: `${animationDelay}ms`,
                zIndex: active
                  ? allButtonsSorted.length + 10
                  : allButtonsSorted.length - index,
              }}
              aria-current={active ? "page" : undefined}
              aria-label={`${group.title} Success Stories`}
              title={group.title}
            >
              <IconComponent
                className="w-5 h-5 flex-shrink-0"
                style={{
                  color: active
                    ? "var(--primary, #003188)"
                    : "rgba(75, 85, 99, 1)",
                }}
                aria-hidden="true"
              />
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
